import {
    ButtonGroup,
    ContractIcon,
    DaButton,
    HeartIcon,
} from '@paygreen/paygreen-ui';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { LineKeyValue } from '../../../../components';
import { useAPIError } from '../../../../context';
import { spaces } from '../../../../utils/constants';
import { apiAuth } from '../../../../utils/hooks';
import { getDataFromObject } from '../../../../utils/tools';
import useSpacesPricing from '../../hooks/useSpacesPricing';
import { CardInfo, EditModalIcon, UnsubscribedWarning } from '../../shared';
import SpaceManagementBillingData from './SpaceManagementBillingData';
import SpaceManagementModal from './SpaceManagementModal';
import SpaceManagementStatus from './SpaceManagementStatus';

const SpaceManagementCard = ({
    t,
    isLoading,
    setIsLoading,
    accountData,
    mode,
    inputList,
    refresh,
}) => {
    const billingName = mode + 'Billing';
    const spaceName = mode === spaces.charity ? 'usesArrondi' : 'usesTree';

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const { accountId } = useParams();
    const { addError } = useAPIError();
    const { spacePrices } = useSpacesPricing(mode, isMounted);
    const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
    const [subscriptionData, setsubscriptionData] = useState(null);

    const isEditable =
        accountData?.[spaceName] === '1' &&
        accountData?.administrator?.enabledAt !== '0';
    const isNotUnsubscribed =
        !accountData?.[billingName] ||
        accountData?.[spaceName] === '1' ||
        accountData?.[billingName]?.unsubscribedAt === '0';

    /**
     * GET request to API to get user's subscription informations
     * @returns {object} - object with price and mode
     */
    const getUserSubscription = useCallback(async () => {
        setIsSubscriptionLoading(true);

        apiAuth()
            .get(`/account/${accountId}/billing/subscription-tariff`, {
                params: { service: mode.toUpperCase() },
            })
            .then(async (res) => {
                if (mode === 'climate') {
                    const configRes = await apiAuth().get(
                        `/climate/config/${accountId}`,
                    );

                    if (isMounted.current) {
                        setsubscriptionData({
                            ...res.data.data,
                            contribution_mode:
                                configRes?.data?.contribution_mode,
                        });
                    }
                } else {
                    isMounted.current && setsubscriptionData(res.data.data);
                }
            })
            .catch((error) => {
                if (
                    error?.response?.data.status === 422 ||
                    error?.response?.data.status === 404
                ) {
                    return [];
                } else {
                    addError(
                        error?.response?.data.detail,
                        error?.response?.data.status,
                    );
                }
            })
            .then(() => {
                if (isMounted.current) {
                    setIsLoading(false);
                    setIsSubscriptionLoading(false);
                }
            });
    }, [addError, mode, accountId, setIsLoading]);

    useEffect(() => {
        isMounted.current = true;

        if (spaceName) {
            getUserSubscription();
        }

        return () => {
            isMounted.current = false;
        };
    }, [getUserSubscription, spaceName]);

    const CardButtons = () => (
        <ButtonGroup align="center" marginTop="sm">
            {accountData?.[billingName]?.status === 'SIGNED' ? (
                <RouterLink
                    to={{
                        pathname: `/admin/users/${accountId}/${mode}-billing`,
                    }}
                >
                    <DaButton
                        buttonStyle="line"
                        gradient="theme"
                        colorTheme={
                            mode === spaces.charity ? 'quaternary' : 'primary'
                        }
                        buttonSize="sm"
                        icon={<ContractIcon />}
                    >
                        {t('user.billing.metaTitle')}
                    </DaButton>
                </RouterLink>
            ) : null}

            {isEditable && mode === spaces.charity ? (
                <RouterLink
                    to={{
                        pathname: `/admin/users/${accountId}/charity-donations`,
                    }}
                >
                    <DaButton
                        buttonStyle="line"
                        gradient="theme"
                        colorTheme="quaternary"
                        buttonSize="sm"
                        icon={<HeartIcon />}
                    >
                        {t('shopDonations.title')}
                    </DaButton>
                </RouterLink>
            ) : null}
        </ButtonGroup>
    );

    return (
        <CardInfo
            isLoading={isLoading || isSubscriptionLoading}
            title={mode.charAt(0).toUpperCase() + mode.slice(1) + 'kit'}
            button={<CardButtons />}
            actionIcon={
                <EditModalIcon
                    title={
                        isEditable
                            ? t('user.space.edit.title')
                            : t('user.space.edit.notEditableTitle')
                    }
                    htmlTag="button"
                    hasHover={isEditable}
                    style={{
                        filter: isEditable ? null : 'grayscale(1)',
                    }}
                >
                    {isEditable && (
                        <SpaceManagementModal
                            accountData={accountData}
                            subscriptionData={subscriptionData}
                            setIsLoading={setIsLoading}
                            mode={mode}
                            refreshAccount={refresh}
                            refreshSubscription={getUserSubscription}
                            spacePrices={spacePrices}
                        />
                    )}
                </EditModalIcon>
            }
        >
            {isNotUnsubscribed ? (
                inputList.map((item, index) => (
                    <LineKeyValue
                        key={index}
                        hasUppercase={false}
                        keyLabel={t(
                            `admin.user.${mode}KitStatus` +
                                '.' +
                                item.name +
                                'Input',
                        )}
                        keyValue={
                            accountData || subscriptionData
                                ? getDataFromObject(item.value, accountData) ||
                                  getDataFromObject(
                                      item.value,
                                      subscriptionData,
                                  ) ||
                                  0
                                : null
                        }
                        textSize="xs"
                    >
                        <SpaceManagementStatus
                            name={item.name}
                            mode={mode}
                            accountData={accountData}
                            subscriptionData={subscriptionData}
                            spaceName={spaceName}
                            billingName={billingName}
                        />

                        {isEditable ? (
                            <SpaceManagementBillingData
                                name={item.name}
                                mode={mode}
                                subscriptionData={subscriptionData}
                            />
                        ) : null}
                    </LineKeyValue>
                ))
            ) : (
                <UnsubscribedWarning />
            )}
        </CardInfo>
    );
};

SpaceManagementCard.propTypes = {
    accountData: PropTypes.object,
    isLoading: PropTypes.bool,
    setIsLoading: PropTypes.func,
    mode: PropTypes.oneOf(Object.values(spaces)).isRequired,
    inputList: PropTypes.array,
    refresh: PropTypes.func,
};

SpaceManagementCard.defaultProps = {
    isLoading: false,
};

export default withTranslation('common')(SpaceManagementCard);
